import React, {useEffect, useRef, useState} from 'react';

import {Checkbox, ListItem, Menu, MenuItem, MenuList, Paper} from "@mui/material";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import ListItemText from "@mui/material/ListItemText";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import { _airports_list } from '../../../sampleData/airline_data';


// Helper function to calculate layover time between two segments
const calculateLayoverTime = (arrivalTime, nextDepartureTime) => {
    const diffMs = new Date(nextDepartureTime) - new Date(arrivalTime); // Difference in milliseconds
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60)); // Convert to hours
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60)); // Remaining minutes
    return `${diffHours > 0 ? `${diffHours} hr${diffHours > 1 ? 's' : ''}` : ''} ${diffMinutes > 0 ? `${diffMinutes} min${diffMinutes > 1 ? 's' : ''}` : ''}`.trim();
};

const formatDuration = (duration) => {
    // If the input is an ISO 8601 duration string (e.g. "PT2H30M")
    if (typeof duration === 'string' && duration.startsWith('P')) {
        const match = duration.match(/P(?:(\d+)D)?T(?:(\d+)H)?(?:(\d+)M)?/);
        const days = parseInt(match[1] || '0', 10);
        const hours = parseInt(match[2] || '0', 10);
        const minutes = parseInt(match[3] || '0', 10);

        const dayText = days > 0 ? `${days} day${days > 1 ? 's' : ''}` : '';
        const hourText = hours > 0 ? `${hours} hr${hours > 1 ? 's' : ''}` : '';
        const minuteText = minutes > 0 ? `${minutes} min${minutes > 1 ? 's' : ''}` : '';

        return [dayText, hourText, minuteText].filter(Boolean).join(' ');
    }

    // If the input is an object with departureTime and arrivalTime (e.g., in the second structure)
    if (duration.depart_timestamp && duration.arrive_timestamp) {
        const departureTime = new Date(duration.depart_timestamp);
        const arrivalTime = new Date(duration.arrive_timestamp);

        // Calculate the difference in milliseconds
        const timeDifference = arrivalTime - departureTime;

        // Convert milliseconds to minutes
        const totalMinutes = Math.floor(timeDifference / (1000 * 60));

        // Calculate hours and minutes
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        const hourText = hours > 0 ? `${hours} hr${hours > 1 ? 's' : ''}` : '';
        const minuteText = minutes > 0 ? `${minutes} min${minutes > 1 ? 's' : ''}` : '';

        return [hourText, minuteText].filter(Boolean).join(' ');
    }

    // If the input doesn't match any format, return an empty string
    return '';
};


const blueShades = [
    '#1E90FF', // DodgerBlue
    '#00BFFF', // DeepSkyBlue
    '#4682B4', // SteelBlue
    '#5F9EA0', // CadetBlue
    '#87CEEB', // SkyBlue
    '#4169E1', // RoyalBlue
    '#6495ED', // CornflowerBlue
    '#6A5ACD', // SlateBlue
    '#00CED1', // DarkTurquoise
    '#7B68EE', // MediumSlateBlue
];




function FlightGoogleMap({
                             _global_map_ref,
                             flightsOnMap,
                             setFlightsOnMap,
                             onSelectFlight,
                             departureAirport,
                             destinationAirport }){


    const airportMarkersRef = useRef([]);
    const flightMarkersRef = useRef([]);

    const [mapMarkerLayers, setMapMarkerLayers] = useState([]); // Store layers of flights
    const [anchorLayersEl, setAnchorLayersEl] = useState(null); // Menu anchor



    // Clear airport markers
    const clearAirportMarkers = () => {
        console.log('clearAirportMarkers');
        airportMarkersRef.current.forEach(marker => marker.setMap(null));
        airportMarkersRef.current = [];
    };

    // Clear flight markers and polylines
    const clearFlightMarkersAndPolylines = () => {
        console.log('clearFlightMarkersAndPolylines');
        flightMarkersRef.current.forEach(marker => marker.setMap(null));
        flightMarkersRef.current = [];
    };

    const addAirportMarker = (map, position, airportCode) => {
        console.log('addAirportMarker');

        const markerContentDiv = document.createElement('div');
        markerContentDiv.style.backgroundColor = 'yellow';
        markerContentDiv.style.width = '12px';
        markerContentDiv.style.height = '12px';
        markerContentDiv.style.borderRadius = '50%';
        markerContentDiv.style.border = '2px solid black';
        markerContentDiv.style.boxShadow = '0 0 5px rgba(0,0,0,0.5)';

        const marker = new window.google.maps.marker.AdvancedMarkerElement({
            position: position,
            map: map,
            content: markerContentDiv,
            title: airportCode, // Display the airport code on hover
        });

        airportMarkersRef.current.push(marker); // Store the airport marker
    };

    // Open/Close layers menu
    const handleButtonOpenLayersMenuClick = (event) => setAnchorLayersEl(event.currentTarget);
    const handleButtonLayersMenuClose = () => setAnchorLayersEl(null);

    const toggleLayerVisibility = (flightId) => {
        setMapMarkerLayers((prevLayers) =>
            prevLayers.map((layer) => {
                if (layer.id === flightId) {
                    layer.visible = !layer.visible;
                }
                return layer;
            })
        );
    };

    // Delete flight layer
    const handleMarkerLayerDeleteClick = (flightId) => {
        console.log('handleMarkerLayerDeleteClick : '+flightId);

        setMapMarkerLayers((prevLayers) => {
            const layerToDelete = prevLayers.find((layer) => layer.id === flightId);
            if (layerToDelete) {
                // Remove markers and polylines from the map
                layerToDelete.markers.forEach((marker) => marker.setMap(null));
                layerToDelete.polylines.forEach((polyline) => polyline.setMap(null));

                // Notify FlightSelection to uncheck the flight
                setFlightsOnMap(flightId);
            }
            return prevLayers.filter((layer) => layer.id !== flightId); // Remove layer
        });
    };

    // Add flight layers to the map
    const addFlightToMapOLD = (flight, index) => {
        console.log('addFlightToMap : ' + flight.id);

        const bounds = new window.google.maps.LatLngBounds();
        const markers = [];
        const polylines = [];

        // Determine the structure of the flight and extract appropriate details
        let airlineName = '';
        let flightNumber = '';

        // Check if the flight is from the first structure (with 'owner' and 'slices')
        if (flight.owner && flight.slices && flight.slices.length > 0) {
            // First structure: Extract airline and flight number from slices
            airlineName = flight.owner.name;
            flightNumber = flight.slices[0]?.segments[0]?.marketing_carrier_flight_number || 'N/A';
        } else if (flight.airline_name) {
            // Second structure: Extract airline and flight number from the flight object
            airlineName = flight.airline_name;
            flightNumber = flight.flight_number || 'N/A';
        }

        const layerName = `${airlineName} # ${flightNumber}`;

        // Check if the flight is already added as a layer
        setMapMarkerLayers(prevLayers => {
            const layerExists = prevLayers.some(layer => layer.id === flight.id);

            if (layerExists) {
                console.log(`Layer ${flight.id} already exists, skipping.`);
                // Make sure the layer is showing
                toggleLayerVisibility(flight.id);

                return prevLayers; // If the layer exists, return the previous state without changes
            }

            // Cycle through the blue shades using the index
            const polylineColor = blueShades[index % blueShades.length];

            // If the layer doesn't exist, proceed to add it
            if (flight.slices && flight.slices.length > 0) {
                // First structure: Process the slices and segments
                flight.slices.forEach((slice) => {
                    slice.segments.forEach((segment, segmentIndex) => {
                        // Find the origin and destination airports in the _airports_list
                        const originAirport = _airports_list.find(airport => airport.iata === segment.origin.iata_code);
                        const destinationAirport = _airports_list.find(airport => airport.iata === segment.destination.iata_code);

                        // Ensure we have valid airports before using their lat/lng
                        if (!originAirport || !destinationAirport) {
                            console.error(`Missing airport data for ${segment.origin.iata_code} or ${segment.destination.iata_code}`);
                            return;
                        }

                        const origin = { lat: parseFloat(originAirport.lat), lng: parseFloat(originAirport.lon) };
                        const destination = { lat: parseFloat(destinationAirport.lat), lng: parseFloat(destinationAirport.lon) };

                        // Calculate the midpoint of the flight path using google.maps.geometry.spherical.interpolate
                        const midpoint = new window.google.maps.geometry.spherical.interpolate(origin, destination, 0.5);

                        // Add departure marker for the first segment (if it's the first flight of the slice)
                        if (segmentIndex === 0) {
                            const departMarkerContentDiv = document.createElement('div');
                            departMarkerContentDiv.innerHTML = `
                                <div class="planning-google-map-marker-layer-item">
                                    <span class="planning-google-map-marker-layer-item-name">
                                        Depart: ${new Date(segment.departing_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
                                    </span>
                                </div>
                            `;
                            const departMarker = new window.google.maps.marker.AdvancedMarkerView({
                                position: origin,
                                map: _global_map_ref.current,
                                content: departMarkerContentDiv,
                                title: `Depart: ${segment.origin.iata_code} - ${new Date(segment.departing_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`,
                            });

                            markers.push(departMarker);
                            bounds.extend(departMarker.position);
                        }

                        // Add layover marker if there's a next segment
                        if (segmentIndex < slice.segments.length - 1) {
                            const nextSegment = slice.segments[segmentIndex + 1];
                            const layoverTime = calculateLayoverTime(segment.arriving_at, nextSegment.departing_at);

                            const layoverMarkerContentDiv = document.createElement('div');
                            layoverMarkerContentDiv.innerHTML = `
                            <div class="planning-google-map-marker-layer-item">
                                <span class="planning-google-map-marker-layer-item-name">
                                   Arrive: ${new Date(segment.arriving_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}<br/>
                                   Layover: ${layoverTime}<br/>
                                   Depart: ${new Date(nextSegment.departing_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
                                </span>
                            </div>
                        `;
                            const layoverMarker = new window.google.maps.marker.AdvancedMarkerView({
                                position: destination,
                                map: _global_map_ref.current,
                                content: layoverMarkerContentDiv,
                                title: `Layover: ${layoverTime}`,
                            });

                            markers.push(layoverMarker);
                            bounds.extend(layoverMarker.position);
                        } else {
                            // If no next segment, add a regular arrival marker
                            const arriveMarkerContentDiv = document.createElement('div');
                            arriveMarkerContentDiv.innerHTML = `
                                <div class="planning-google-map-marker-layer-item">
                                    <span class="planning-google-map-marker-layer-item-name">
                                       Arrive: ${new Date(segment.arriving_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
                                    </span>
                                </div>
                            `;
                            const arriveMarker = new window.google.maps.marker.AdvancedMarkerView({
                                position: destination,
                                map: _global_map_ref.current,
                                content: arriveMarkerContentDiv,
                                title: `Arrive: ${new Date(segment.arriving_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`,
                            });

                            markers.push(arriveMarker);
                            bounds.extend(arriveMarker.position);
                        }

                        // Draw the flight path for this segment
                        const flightPath = new window.google.maps.Polyline({
                            path: [origin, destination],
                            geodesic: true,
                            strokeColor: polylineColor,
                            strokeOpacity: 1.0,
                            strokeWeight: 2,
                            map: _global_map_ref.current,
                        });
                        polylines.push(flightPath);

                        // Calculate the flight duration and format it (assuming `segment.duration` is in ISO 8601 format)
                        const formattedDuration = formatDuration(segment.duration);

                        // Create a label or marker at the midpoint
                        const midpointMarkerContentDiv = document.createElement('div');
                        midpointMarkerContentDiv.innerHTML = `
                            <div class="planning-google-map-marker-layer-item">
                                <span class="planning-google-map-marker-layer-item-name">
                                    ${formattedDuration}
                                </span>
                            </div>
                        `;
                        const midpointMarker = new window.google.maps.marker.AdvancedMarkerView({
                            position: midpoint,
                            map: _global_map_ref.current,
                            content: midpointMarkerContentDiv,
                            title: `Flight ${flightNumber}`,
                        });

                        markers.push(midpointMarker);

                        bounds.extend(midpointMarker.position);
                    });
                });
            } else if (flight.airline_name) {
                // Second structure: Directly use the available data
                const originAirport = flight.depart_airport_name;
                const destinationAirport = flight.arrive_airport_name;

                if (!originAirport || !destinationAirport) {
                    console.error(`Missing airport data for ${flight.depart_airport_name} or ${flight.arrive_airport_name}`);
                    return;
                }

                const origin = { lat: parseFloat(flight.depart_airport_latitude), lng: parseFloat(flight.depart_airport_longitude) };
                const destination = { lat: parseFloat(flight.arrive_airport_latitude), lng: parseFloat(flight.arrive_airport_longitude) };

                // Calculate the midpoint of the flight path using google.maps.geometry.spherical.interpolate
                const midpoint = new window.google.maps.geometry.spherical.interpolate(origin, destination, 0.5);

                const departMarkerContentDiv = document.createElement('div');
                departMarkerContentDiv.innerHTML = `
                                <div class="planning-google-map-marker-layer-item">
                                    <span class="planning-google-map-marker-layer-item-name">
                                        Depart: ${new Date(flight.depart_timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
                                    </span>
                                </div>
                            `;
                const departMarker = new window.google.maps.marker.AdvancedMarkerView({
                    position: origin,
                    map: _global_map_ref.current,
                    content: departMarkerContentDiv,
                    title: `Depart: ${originAirport} - ${new Date(flight.depart_timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`,
                });

                markers.push(departMarker);
                bounds.extend(departMarker.position);

                const arriveMarkerContentDiv = document.createElement('div');
                arriveMarkerContentDiv.innerHTML = `
                                <div class="planning-google-map-marker-layer-item">
                                    <span class="planning-google-map-marker-layer-item-name">
                                       Arrive: ${new Date(flight.arrive_timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
                                    </span>
                                </div>
                            `;
                const arriveMarker = new window.google.maps.marker.AdvancedMarkerView({
                    position: destination,
                    map: _global_map_ref.current,
                    content: arriveMarkerContentDiv,
                    title: `Arrive: ${new Date(flight.arrive_timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`,
                });

                markers.push(arriveMarker);
                bounds.extend(arriveMarker.position);

                // Create markers and polyline for the second structure
                const polylineColor = blueShades[index % blueShades.length];

                // Draw a flight path and markers (similar to first structure but with direct flight details)
                const flightPath = new window.google.maps.Polyline({
                    path: [origin, destination],
                    geodesic: true,
                    strokeColor: polylineColor,
                    strokeOpacity: 1.0,
                    strokeWeight: 2,
                    map: _global_map_ref.current,
                });
                polylines.push(flightPath);

                // Calculate the flight duration and format it (assuming `segment.duration` is in ISO 8601 format)
                const formattedDuration = formatDuration(flight);

                // Create a label or marker at the midpoint
                const midpointMarkerContentDiv = document.createElement('div');
                midpointMarkerContentDiv.innerHTML = `
                            <div class="planning-google-map-marker-layer-item">
                                <span class="planning-google-map-marker-layer-item-name">
                                    ${formattedDuration}
                                </span>
                            </div>
                        `;
                const midpointMarker = new window.google.maps.marker.AdvancedMarkerView({
                    position: midpoint,
                    map: _global_map_ref.current,
                    content: midpointMarkerContentDiv,
                    title: `Flight ${flightNumber}`,
                });

                markers.push(midpointMarker);

                bounds.extend(midpointMarker.position);
            }

            // Adjust the map bounds to include all markers for this flight
            _global_map_ref.current.fitBounds(bounds);

            // Store markers and polylines for this flight
            flightMarkersRef.current.push(...markers);
            flightMarkersRef.current.push(...polylines);

            // Add the new layer if it doesn't already exist
            return [
                ...prevLayers,
                {
                    id: flight.id, // Assign flight id here
                    name: layerName, // Set the layer name as airline name and flight number
                    visible: true,
                    markers: markers,
                    polylines: polylines
                }
            ];
        });
    };

    const _default_map_options = {
        mapId: "6c79697cd5555167",
        center: departureAirport
            ? { lat: parseFloat(departureAirport.lat), lng: parseFloat(departureAirport.lon) }
            : { lat: 37.422222, lng: -122.083333 }, // Default to a location if no airport is selected
        zoom: 10,
        mapTypeControl: false,
        fullscreenControl: false,
    };

    // Store flight-related map objects (markers and polylines) in a map for easy removal
    const flightMapObjects = {};

    useEffect(() => {

        if (!window.google || !window.google.maps) {
            console.error("Google Maps API is not available.");
            return;
        }

        if (usePA_GlobalStore.getState().PA_GlobalObject.Planning
            && usePA_GlobalStore.getState().PA_GlobalObject.Planning.locations
            && usePA_GlobalStore.getState().PA_GlobalObject.Planning.locations.length > 0 ){

            _global_map_ref.current = new window.google.maps.Map(
                document.getElementById("global_google_map_holder"),
                {
                    mapId: "6c79697cd5555167",
                    center: {
                        lat: usePA_GlobalStore.getState().PA_GlobalObject.Planning.locations[0].latitude,
                        lng: usePA_GlobalStore.getState().PA_GlobalObject.Planning.locations[0].longitude,
                    },
                    zoom: 10,
                    mapTypeControl: false,
                    fullscreenControl: false
                }
            );
        } else {
            _global_map_ref.current = new window.google.maps.Map(
                document.getElementById("global_google_map_holder"),
                _default_map_options
            );

        }

        if (flightsOnMap.length && _global_map_ref && _global_map_ref.current) {

            // Function to clear markers and polylines of a specific flight
            const clearFlightFromMap = (flightId) => {
                if (flightMapObjects[flightId]) {
                    flightMapObjects[flightId].markers.forEach(marker => marker.setMap(null));
                    flightMapObjects[flightId].polylines.forEach(polyline => polyline.setMap(null));
                    delete flightMapObjects[flightId]; // Remove the entry from the map
                }
            };

            // Function to clear all existing flight markers and polylines
            const clearMap = () => {
                Object.keys(flightMapObjects).forEach(flightId => {
                    clearFlightFromMap(flightId);
                });
            };

            // Clear previous markers and polylines
            clearMap();

            // Add the selected flights to the map
//            flightsOnMap.forEach(addFlightToMap);

            // Clean up on component unmount or when selected flights change
            return () => {
                clearMap();
            };


        }
    }, [_global_map_ref]);

    useEffect(() => {
        if (_global_map_ref.current) {
            const map = _global_map_ref.current;

            clearAirportMarkers(); // Clear existing airport markers

            const bounds = new window.google.maps.LatLngBounds();

            // Add markers for selected airports
            if (departureAirport) {
                const departurePosition = {
                    lat: parseFloat(departureAirport.lat),
                    lng: parseFloat(departureAirport.lon),
                };
                addAirportMarker(map, departurePosition, departureAirport.iata);
                bounds.extend(departurePosition);
            }

            if (destinationAirport) {
                const destinationPosition = {
                    lat: parseFloat(destinationAirport.lat),
                    lng: parseFloat(destinationAirport.lon),
                };
                addAirportMarker(map, destinationPosition, destinationAirport.iata);
                bounds.extend(destinationPosition);
            }

            // Adjust bounds for airports
            if (departureAirport || destinationAirport) {
                map.fitBounds(bounds);
            }
        }
    }, [departureAirport, destinationAirport]);

    // Handle Flight Markers (flightsOnMap) useEffect
    useEffect(() => {
        console.log("flightsOnMap has changed!!");

//        clearFlightMarkersAndPolylines(); // Clear existing flight markers and polylines

//        setMapMarkerLayers([]);

        // Add selected flights as markers and polylines
        addFlightLayersToMap(flightsOnMap);
//        addFlightToMap(flightsOnMap);
//        flightsOnMap.forEach((flight, index) => {
//            addFlightToMap(flight, index);
//        });

    }, [flightsOnMap]);


    const addFlightLayersToMap = (flightList) => {

        const bounds = new window.google.maps.LatLngBounds();

        console.log("mapMarkerLayers", mapMarkerLayers);

        const newLayers = flightList.map((flight, index) => {

            console.log("looking for existing flight", flight);

            //see if flight exists before adding
            if (mapMarkerLayers.some(_layer => _layer.id === flight.id)) return null;

            console.log("NEW FLIGHT TO ADD");
            // Determine the structure of the flight and extract appropriate details
            let airlineName = '';
            let flightNumber = '';

            let origin = '';
            let destination = '';

            // Check if the flight is from the first structure (with 'owner' and 'slices')
            if (flight.owner && flight.slices && flight.slices.length > 0) {
                // First structure: Extract airline and flight number from slices
                airlineName = flight.owner.name;
                flightNumber = flight.slices[0]?.segments[0]?.marketing_carrier_flight_number || 'N/A';

                const originAirport = _airports_list.find(airport => airport.iata === flight.slices[0]?.segments[0]?.origin.iata_code);
                const destinationAirport = _airports_list.find(airport => airport.iata === flight.slices[0]?.segments[0]?.destination.iata_code);

                // Ensure we have valid airports before using their lat/lng
                if (!originAirport || !destinationAirport) {
                    console.error(`Missing airport data for ${flight.slices[0]?.segments[0]?.origin.iata_code} or ${flight.slices[0]?.segments[0]?.destination.iata_code}`);
                    return;
                }
                origin = { lat: parseFloat(originAirport.lat), lng: parseFloat(originAirport.lon) };
                destination = { lat: parseFloat(destinationAirport.lat), lng: parseFloat(destinationAirport.lon) };

            } else if (flight.airline_name) {
                // Second structure: Extract airline and flight number from the flight object
                airlineName = flight.airline_name;
                flightNumber = flight.flight_number || 'N/A';

                origin = { lat: parseFloat(flight.depart_airport_latitude), lng: parseFloat(flight.depart_airport_longitude) };
                destination = { lat: parseFloat(flight.arrive_airport_latitude), lng: parseFloat(flight.arrive_airport_longitude) };

            }

            // Extend bounds to include this flight's points
            bounds.extend(origin);
            bounds.extend(destination);

            const layerName = `${airlineName} # ${flightNumber}`;
            const polylineColor = blueShades[index % blueShades.length];

            const flightPath = new window.google.maps.Polyline({
                path: [origin, destination],
                geodesic: true,
                strokeColor: polylineColor,
                strokeOpacity: 1.0,
                strokeWeight: 2,
                map: _global_map_ref.current,
            });

            return {
                id: flight.id,
                name: layerName, // Set the layer name as airline name and flight number
                flight: flight,
                visible: true,
                polyline: [flightPath],
                markers: [], // empty initially
            };

        }).filter(layer => layer); // remove null entries


        // Fit map to show all polylines
        if (newLayers.length > 0) {
            _global_map_ref.current.fitBounds(bounds);
        }

        console.log("newLayers : ", newLayers);
        setMapMarkerLayers(prevLayers => [...prevLayers, ...newLayers]);

    };

    useEffect(() => {
        console.log("mapMarkerLayers has changed");

        // Go through each layer
        mapMarkerLayers.forEach((layer) => {

            console.log("layer.visible : ", layer.visible);

           if (layer.visible){
               console.log("create markers for this visible layer");

               // Toggle polyline visibility
               layer.polyline.forEach((polyline) => {
                   polyline.setMap(_global_map_ref.current);  // Re-add polyline to the map
               });


               // create markers for this visible layer
               const flight = layer.flight;
               if (!flight) return;

               let origin, destination;
               if (flight.owner && flight.slices && flight.slices.length > 0) {
                   const originAirport = _airports_list.find(a => a.iata === flight.slices[0].segments[0].origin.iata_code);
                   const destinationAirport = _airports_list.find(a => a.iata === flight.slices[0].segments[0].destination.iata_code);
                   if (!originAirport || !destinationAirport) return;

                   origin = { lat: parseFloat(originAirport.lat), lng: parseFloat(originAirport.lon) };
                   destination = { lat: parseFloat(destinationAirport.lat), lng: parseFloat(destinationAirport.lon) };
               } else {
                   origin = { lat: parseFloat(flight.depart_airport_latitude), lng: parseFloat(flight.depart_airport_longitude) };
                   destination = { lat: parseFloat(flight.arrive_airport_latitude), lng: parseFloat(flight.arrive_airport_longitude) };
               }

               // Midpoint marker
               const midLat = (origin.lat + destination.lat) / 2;
               const midLng = (origin.lng + destination.lng) / 2;
               const durationMinutes = Math.round((new Date(flight.arrive_timestamp) - new Date(flight.depart_timestamp)) / 60000);
               const hours = Math.floor(durationMinutes / 60);
               const minutes = durationMinutes % 60;

               console.log("layer.markers[1] : ", layer.markers[1]);

               const markerContent = createMarkerDiv(`${hours}h ${minutes}m`);
               const midpointMarker = new window.google.maps.marker.AdvancedMarkerView({
                   position: { lat: midLat, lng: midLng },
                   map: _global_map_ref.current,
                   content: markerContent
               });
               if (layer.markers[1]) {
                   layer.markers[1].map = null;
               }
               layer.markers[1] = midpointMarker;

               // Departure marker - the depart marker has more info if there was a previous flight
               // and this one would be considered a connecting flight
               const previousConnectingFlight = mapMarkerLayers.find(l =>
                   l.visible &&
                   l.flight.arrive_airport_code === flight.depart_airport_code &&
                   l.flight.attendees?.some(att => flight.attendees?.map(a => a.id).includes(att.id))
               );
                console.log("is there a previous flight that would make this a connecting flight : ", previousConnectingFlight);
               console.log("layer.markers[0] : ", layer.markers[0]);
               if (previousConnectingFlight) {
                   console.log("IS a connecting flight");
                   const arriveTime = new Date(previousConnectingFlight.flight.arrive_timestamp);
                   const departTime = new Date(flight.depart_timestamp);
                   const layoverMinutes = Math.round((departTime - arriveTime) / 60000);
                   const layoverHours = Math.floor(layoverMinutes / 60);
                   const layoverRemainMinutes = layoverMinutes % 60;

                   const markerContent = createMarkerDiv(`
                    Arrive: ${arriveTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}<br/>
                    Layover: ${layoverHours}h ${layoverRemainMinutes}m<br/>
                    Depart: ${departTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`);

                   const connectingMarker = new window.google.maps.marker.AdvancedMarkerView({
                       position: origin,
                       map: _global_map_ref.current,
                       content: markerContent
                   });

                   if (layer.markers[0]){
                       layer.markers[0].map = null;
                   }

                   layer.markers[0] = connectingMarker;

               } else {
                   console.log("no flight before this");
                   const markerContent = createMarkerDiv(`Depart: ${new Date(flight.depart_timestamp).toLocaleTimeString([], 
                       { hour: 'numeric', minute: '2-digit', hour12: true })}`);
                   const departMarker = new window.google.maps.marker.AdvancedMarkerView({
                       position: origin,
                       map: _global_map_ref.current,
                       content: markerContent
                   });

                   if (layer.markers[0]){
                       layer.markers[0].map = null;
                   }
                   layer.markers[0] = departMarker;
               }



               // Arrival / connecting marker logic
               const nextConnectingFlight = mapMarkerLayers.find(l =>
                   l.visible &&
                   l.flight.depart_airport_code === flight.arrive_airport_code &&
                   l.flight.attendees?.some(att => flight.attendees?.map(a => a.id).includes(att.id))
               );
               console.log("is there a connecting flight that comes after this : ", nextConnectingFlight);
               console.log("layer.markers[2] : ", layer.markers[2]);
               if (nextConnectingFlight) {
                   console.log("there is a connecting flight");
                   //there is a connecting flight so we actually don't put a marker here
                   if (layer.markers[2]){
                       layer.markers[2].map = null;
                   }
                   layer.markers[2] = null;
               } else {
                   console.log("no connecting flight");
                   const markerContent = createMarkerDiv(`Arrive: ${new Date(flight.arrive_timestamp).toLocaleTimeString([],
                       { hour: 'numeric', minute: '2-digit', hour12: true })}`);
                   const arriveMarker = new window.google.maps.marker.AdvancedMarkerView({
                       position: destination,
                       map: _global_map_ref.current,
                       content: markerContent
                   });
                   if (layer.markers[2]){
                       layer.markers[2].map = null;
                   }
                   layer.markers[2] = arriveMarker;
               }


           } else {
               console.log("this layer should have already removed the map from the markers and the polyline");

               // remove polyline visibility
               layer.polyline.forEach((polyline) => {
                   polyline.setMap(null);  // remove polyline from the map
               });

               // remove marker visibility
               layer.markers?.forEach(marker => marker.map = null);
           }

        });

    }, [mapMarkerLayers]);





    // Simple helper to create a div for marker content
    const createMarkerDiv = (html) => {
        const div = document.createElement('div');
        div.className = "planning-google-map-marker-layer-item";
        div.innerHTML = `<span class="planning-google-map-marker-layer-item-name">${html}</span>`;
        return div;
    };







    return (

        <div style={{position:'relative'}}>
            {/* Map container */}
            <div
                ref={_global_map_ref}
                className="planning-map-area-google-map-holder"
                id="global_google_map_holder"
                style={{width: '100%', height: 'calc(100vh - 243px)'}}
            />

            {/* Add layers control */}
            <div style={{position:'absolute', top: '5px', right : '5px'}}>
                <Paper component="form" sx={{p: '2px 4px', display: 'flex', alignItems: 'center'}}>
                    <Badge badgeContent={mapMarkerLayers.length} color="primary">
                        <IconButton
                            type="button"
                            sx={{p: '10px'}}
                            aria-label="map layers for Markers"
                            onClick={handleButtonOpenLayersMenuClick}
                        >
                            <i className="icon-layers" title="Selected Flights"/>
                        </IconButton>
                    </Badge>
                    <Menu
                        id="split-button-menu"
                        anchorEl={anchorLayersEl}
                        open={Boolean(anchorLayersEl)}
                        onClose={handleButtonLayersMenuClose}
                        transformOrigin={{horizontal: 'right', vertical: 'top'}}
                        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                    >
                        <MenuList dense sx={{ cursor: 'default' }}>
                            {mapMarkerLayers.length > 0 ? (
                                mapMarkerLayers.map((layer) => (
                                    <ListItem key={layer.id} sx={{ cursor: 'default' }}>
                                        <ListItemText>{layer.name}</ListItemText>
                                        <Checkbox
                                            edge="end"
                                            checked={layer.visible}
                                            inputProps={{ 'aria-labelledby': "Toggle Layer" }}
                                            onClick={() => toggleLayerVisibility(layer.id)}
                                        />
                                        <i
                                            style={{ color: 'red', marginLeft: '10px', cursor: 'pointer' }}
                                            className="icon-trash"
                                            onClick={() => handleMarkerLayerDeleteClick(layer.id)}
                                        />
                                    </ListItem>
                                ))
                            ) : (
                                <MenuItem>
                                    <ListItemText>No Layers</ListItemText>
                                </MenuItem>
                            )}
                        </MenuList>

                    </Menu>
                </Paper>
            </div>
        </div>
    )

}

export default FlightGoogleMap;